/**
 * Shared dependencies
 */
import type { StoreFunctions } from '../../shared/types/store';
import type { RecipeListItem } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
import { addItemsToUserList, type AddItemsToUserListItem } from '../requests';

const getListItem = (
	items: RecipeListItem[],
	item: AddItemsToUserListItem
): RecipeListItem | undefined => {
	return items.find((listItem) => listItem.matterId === item.matterId);
};

/**
 * Adds a single item to a user's list and updates the store accordingly.
 *
 * @param fn - Function to update the store state.
 * @param listId - The ID of the list to fetch items for.
 * @param item - The item to add to the list.
 * @return Promise that resolves to an array of list items.
 */
export async function listsAddSingleItem(
	fn: StoreFunctions,
	listId: string,
	item: AddItemsToUserListItem
): Promise<RecipeListItem> {
	const {
		events: { RecipeEvent },
	} = window.valioApp;

	const response = await addItemsToUserList({ listId, items: [item] });

	// Update the items for this specific list in the store
	fn.set((state) => {
		const list = state.data.lists[listId];
		if (!list) {
			return state;
		}

		return {
			data: {
				lists: {
					...state.data.lists,
					[listId]: {
						...list,
						items: [...list.items, ...response],
					},
				},
			},
		};
	});

	// Trigger analytics event for adding item to list
	const listItem = getListItem(response, item);

	if (!listItem) {
		throw new Error('Failed to retrieve the added list item from the response.');
	}

	RecipeEvent.save({
		listId,
		items: [listItem],
		lastSavedItem: listItem,
	});

	return listItem;
}
